// Level bands of each health indicator for charts (namat#112): the value
// ranges where readIndicators (namatAssessment.ts) gives good, acceptable or
// bad, built from INDICATOR_LIMITS and the BMI rules there. Nothing here
// decides a level; it only draws where the levels are. Identical in web and
// mobile (src/lib/assessment/indicatorBands.ts); change both.
//
// Waist, neck and fitness age (#56) have bands for adults (18 and over, or age
// unknown for waist and neck) whose sex is recorded, as readIndicators judges
// them; fitness age's bands sit around the real age, so they need a birth date.

import { CDC_BMI_BOYS, CDC_BMI_GIRLS } from './cdcBmiForAge';
import { ADULT_AGE_MONTHS, DEFAULT_CHILD_SEX, INDICATOR_LIMITS, type Level, type Sex } from './namatAssessment';

/** Values from `from` up to `to`; `to` may be Infinity. */
export interface LevelBand {
  level: Level;
  from: number;
  to: number;
}

/** One list of bands per plotted line; blood pressure has [systolic, diastolic]. */
export type IndicatorBands = LevelBand[][];

const steps = (cuts: number[], levels: Level[]): LevelBand[] =>
  levels.map((level, i) => ({ level, from: i === 0 ? 0 : cuts[i - 1], to: i < cuts.length ? cuts[i] : Infinity }));

/** BMI bands: CDC percentiles from 2 up to 20 years, adult ranges otherwise; none under 2. */
function bmiBands(ageMonths: number | null, sex: Sex | null | undefined): LevelBand[] | null {
  if (ageMonths !== null && ageMonths < 24) return null;
  if (ageMonths !== null && ageMonths < 240) {
    const table = (sex || DEFAULT_CHILD_SEX) === 'male' ? CDC_BMI_BOYS : CDC_BMI_GIRLS;
    const target = ageMonths + 0.5;
    const row = table.reduce((found, r) => (r[0] <= target ? r : found), table[0]);
    const [, , , , p5, p85, p95] = row;
    return steps([p5, p85, p95], ['acceptable', 'good', 'acceptable', 'bad']);
  }
  return steps([18.5, 25, 30], ['acceptable', 'good', 'acceptable', 'bad']);
}

/**
 * The bands of one indicator for a person of `ageMonths` (null = adult), or
 * null when it has none (BMI under 2; waist, neck and fitness age for a child
 * or without a sex; fitness age without an age; an indicator without limits).
 */
export function indicatorBands(
  key: string,
  person?: { ageMonths: number | null; sex?: Sex | null }
): IndicatorBands | null {
  const ageMonths = person?.ageMonths ?? null;
  switch (key) {
    case 'bmi': {
      const bands = bmiBands(ageMonths, person?.sex);
      return bands ? [bands] : null;
    }
    case 'bloodPressure': {
      const { acceptable, bad } = INDICATOR_LIMITS.bloodPressure;
      return [
        steps([acceptable.systolic, bad.systolic], ['good', 'acceptable', 'bad']),
        steps([acceptable.diastolic, bad.diastolic], ['good', 'acceptable', 'bad']),
      ];
    }
    case 'hba1c':
    case 'cholesterol': {
      const { acceptable, bad } = INDICATOR_LIMITS[key];
      return [steps([acceptable, bad], ['good', 'acceptable', 'bad'])];
    }
    case 'pulse': {
      const { good, acceptable } = INDICATOR_LIMITS.pulse;
      return [steps([acceptable[0], good[0], good[1], acceptable[1]], ['bad', 'acceptable', 'good', 'acceptable', 'bad'])];
    }
    case 'waistCircumference':
    case 'neckCircumference':
    case 'fitnessAge': {
      const sex = person?.sex === 'male' || person?.sex === 'female' ? person.sex : null;
      if (!sex || (ageMonths !== null && ageMonths < ADULT_AGE_MONTHS)) return null;
      if (key === 'waistCircumference') {
        const { acceptable, bad } = INDICATOR_LIMITS.waistCircumference[sex];
        return [steps([acceptable, bad], ['good', 'acceptable', 'bad'])];
      }
      if (key === 'neckCircumference') {
        return [steps([INDICATOR_LIMITS.neckCircumference[sex].bad], ['good', 'bad'])];
      }
      // Fitness age: good up to the real age, acceptable up to 5 years older.
      if (ageMonths === null) return null;
      const realAge = Math.floor(ageMonths / 12);
      return [steps([realAge, realAge + INDICATOR_LIMITS.fitnessAge.acceptableYearsOver], ['good', 'acceptable', 'bad'])];
    }
    default:
      return null;
  }
}

/** How far `value` is from the nearest good band (0 inside one); null without a good band. */
export function distanceToGood(bands: LevelBand[], value: number): number | null {
  let best: number | null = null;
  for (const b of bands) {
    if (b.level !== 'good') continue;
    const d = value < b.from ? b.from - value : value > b.to ? value - b.to : 0;
    if (best === null || d < best) best = d;
  }
  return best;
}
